/**
 * @fileoverview Zod schemas for local contact storage and engagement tracking.
 * These schemas define the shape of Contact and ContactEvent records
 * stored in Ampt Data (not SendGrid API shapes — see sendgrid.js for those).
 */

import { z } from '@hono/zod-openapi';

// ── Nested Schemas ──

export const ContactEngagementSchema = z
  .object({
    totalDelivered: z.number().int().default(0),
    totalOpens: z.number().int().default(0),
    totalClicks: z.number().int().default(0),
    lastOpenedAt: z.string().datetime().nullable().default(null),
    lastClickedAt: z.string().datetime().nullable().default(null),
    lastDeliveredAt: z.string().datetime().nullable().default(null),
  })
  .openapi('ContactEngagement');

export const ContactSuppressionSchema = z
  .object({
    bounced: z.boolean().default(false),
    bouncedAt: z.string().datetime().nullable().default(null),
    bounceReason: z.string().nullable().default(null),
    blocked: z.boolean().default(false),
    blockedAt: z.string().datetime().nullable().default(null),
    invalidEmail: z.boolean().default(false),
    invalidEmailAt: z.string().datetime().nullable().default(null),
    unsubscribed: z.boolean().default(false),
    unsubscribedAt: z.string().datetime().nullable().default(null),
    globalUnsubscribed: z.boolean().default(false),
    globalUnsubscribedAt: z.string().datetime().nullable().default(null),
    spamReported: z.boolean().default(false),
    spamReportedAt: z.string().datetime().nullable().default(null),
    suppressionGroups: z.array(z.number().int()).default([]),
  })
  .openapi('ContactSuppression');

export const ContactStatusHistorySchema = z
  .object({
    status: z.string(),
    changedAt: z.string().datetime(),
    source: z.string(),
  })
  .openapi('ContactStatusHistory');

export const ContactSyncSchema = z
  .object({
    lastSyncedAt: z.string().datetime().nullable().default(null),
    lastSyncSource: z
      .enum(['initial', 'quick', 'webhook', 'suppression', 'daily'])
      .nullable()
      .default(null),
    sgCreatedAt: z.string().datetime().nullable().default(null),
    sgUpdatedAt: z.string().datetime().nullable().default(null),
  })
  .openapi('ContactSync');

// ── Contact Record ──

export const LocalContactStatusEnum = z.enum([
  'subscribed',
  'unsubscribed',
  'bounced',
  'spam_reported',
  'unknown',
]);

export const LocalContactSchema = z
  .object({
    email: z.string().email(),
    sgContactId: z.string().nullable().default(null),
    firstName: z.string().nullable().default(null),
    lastName: z.string().nullable().default(null),
    status: LocalContactStatusEnum.default('unknown'),
    customFields: z.record(z.string(), z.any()).default({}),
    listIds: z.array(z.string()).default([]),
    engagement: ContactEngagementSchema.default({}),
    suppression: ContactSuppressionSchema.default({}),
    statusHistory: z.array(ContactStatusHistorySchema).default([]),
    sync: ContactSyncSchema.default({}),
    created: z.string().datetime(),
    modified: z.string().datetime(),
  })
  .openapi('LocalContact');

export const CreateLocalContactSchema = LocalContactSchema.omit({
  created: true,
  modified: true,
}).openapi('CreateLocalContact');

export const UpdateLocalContactSchema = CreateLocalContactSchema.partial().openapi(
  'UpdateLocalContact'
);

// ── ContactEvent Record ──

export const ContactEventTypeEnum = z.enum([
  'processed',
  'dropped',
  'delivered',
  'deferred',
  'bounce',
  'open',
  'click',
  'spam_report',
  'unsubscribe',
  'group_unsubscribe',
  'group_resubscribe',
]);

export const ContactEventSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    eventType: ContactEventTypeEnum,
    timestamp: z.string().datetime(),
    sgEventId: z.string().nullable().default(null),
    sgMessageId: z.string().nullable().default(null),
    url: z.string().nullable().default(null),
    reason: z.string().nullable().default(null),
    bounceType: z.enum(['hard', 'soft', 'blocked']).nullable().default(null),
    userAgent: z.string().nullable().default(null),
    ip: z.string().nullable().default(null),
    issueNumber: z.number().int().nullable().default(null),
    subject: z.string().nullable().default(null),
    campaignId: z.string().nullable().default(null),
    campaignName: z.string().nullable().default(null),
    singlesendId: z.string().nullable().default(null),
    singlesendName: z.string().nullable().default(null),
    templateId: z.string().nullable().default(null),
    templateVersionId: z.string().nullable().default(null),
    urlOffset: z.object({ index: z.number(), type: z.string() }).nullable().default(null),
    response: z.string().nullable().default(null),
    status: z.string().nullable().default(null),
    attemptNum: z.number().int().nullable().default(null),
    asmGroupId: z.number().int().nullable().default(null),
    category: z.array(z.string()).nullable().default(null),
    uniqueArgs: z.record(z.string(), z.any()).nullable().default(null),
    created: z.string().datetime(),
  })
  .openapi('ContactEvent');

export const CreateContactEventSchema = ContactEventSchema.omit({
  id: true,
  created: true,
}).openapi('CreateContactEvent');

// ── SendGrid Webhook Event (raw inbound shape) ──

export const SendGridWebhookEventSchema = z
  .object({
    email: z.string().email(),
    event: z.string(),
    timestamp: z.number(),
    sg_event_id: z.string().optional(),
    sg_message_id: z.string().optional(),
    url: z.string().optional(),
    reason: z.string().optional(),
    type: z.string().optional(),
    useragent: z.string().optional(),
    ip: z.string().optional(),
    subject: z.string().optional(),
    marketing_campaign_id: z.union([z.string(), z.number()]).optional(),
    marketing_campaign_name: z.string().optional(),
    singlesend_id: z.string().optional(),
    singlesend_name: z.string().optional(),
    template_id: z.string().optional(),
    template_version_id: z.string().optional(),
    url_offset: z.object({ index: z.number(), type: z.string() }).optional(),
    response: z.string().optional(),
    status: z.string().optional(),
    attempt_num: z.number().optional(),
    asm_group_id: z.number().optional(),
    category: z.union([z.array(z.string()), z.string()]).optional(),
    unique_args: z.record(z.string(), z.any()).optional(),
  })
  .passthrough()
  .openapi('SendGridWebhookEvent');
